import React, { createContext, useContext, useMemo } from 'react';
import some from 'lodash/some';
import includes from 'lodash/includes';
import { useLocation } from 'react-router-dom';
import { useVenues } from '../../providers/venues';

export const BANNER_HEIGHT = 64;
export const BANNER_GAP = 0;
export const PRIMARY_BAR_HEIGHT = 64;
export const SECONDARY_BAR_HEIGHT = 42;

type NavLayoutValue = {
  trialBannerVisible: boolean;
  pastDueBannerVisible: boolean;
  secondaryBarVisible: boolean;
  primaryHeightPx: number;
  bannerHeightPx: number;
  headerHeightPx: number;
};

const defaultValue: NavLayoutValue = {
  trialBannerVisible: false,
  pastDueBannerVisible: false,
  secondaryBarVisible: false,
  primaryHeightPx: PRIMARY_BAR_HEIGHT,
  bannerHeightPx: BANNER_HEIGHT,
  headerHeightPx: PRIMARY_BAR_HEIGHT,
};

const NavLayoutContext = createContext<NavLayoutValue>(defaultValue);

export const useNavLayout = () => useContext(NavLayoutContext);

export const NavLayoutProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { venues } = useVenues();
  const { pathname } = useLocation();

  const value = useMemo<NavLayoutValue>(() => {
    const venueList = venues ?? [];

    const hasPlanTierTrial = some(venueList, (v) =>
      Boolean(v.activePlanTierTrial?.endDate)
    );
    const hasInitialFreeTrial = some(
      venueList,
      (v) =>
        v.subscription?.tier === 'trial' && Boolean(v.subscription?.endDate)
    );
    const firstVenueStarter = venueList[0]?.subscription?.tier === 'starter';
    const trialBannerVisible =
      hasPlanTierTrial || hasInitialFreeTrial || firstVenueStarter;

    const pastDueBannerVisible = some(venueList, (v) =>
      includes(['unpaid', 'past_due'], v.subscription?.status)
    );

    const secondaryBarVisible = /^\/reports/.test(pathname);

    const visibleBannerCount =
      (trialBannerVisible ? 1 : 0) + (pastDueBannerVisible ? 1 : 0);
    const anyBannerVisible = visibleBannerCount > 0;

    const headerHeightPx =
      PRIMARY_BAR_HEIGHT +
      visibleBannerCount * BANNER_HEIGHT +
      (anyBannerVisible ? BANNER_GAP : 0) +
      (secondaryBarVisible ? SECONDARY_BAR_HEIGHT : 0);

    return {
      trialBannerVisible,
      pastDueBannerVisible,
      secondaryBarVisible,
      primaryHeightPx: PRIMARY_BAR_HEIGHT,
      bannerHeightPx: BANNER_HEIGHT,
      headerHeightPx,
    };
  }, [venues, pathname]);

  return (
    <NavLayoutContext.Provider value={value}>
      {children}
    </NavLayoutContext.Provider>
  );
};
